import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowPastDates", "allowWeekends", "aria-describedby", "aria-label", "aria-labelledby", "className", "customErrorMessage", "customHelpText", "dateFormat", "disabled", "disabledDays", "fwdRef", "initialDate", "inputProps", "invalid", "isDayBlocked", "isOutsideRange", "maxDate", "minDate", "onChange", "required", "showHelpText", "size", "value"];
import React, { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { FormContext, FormRowContext, useLocale } from '@op/opds.opux';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { checkIsDayBlocked, checkIsOutsideRange, checkYearLength, getErrorMessageAndType, validateDate } from './_utils';
import TextInput from '../text-input/text-input';
import InputError from '../input-error/input-error';
import messages from './_messages';
const defaultDateFormat = 'd.M.yyyy';
/**
 * _A specialized input field for entering a known date._
 *
 * ***
 *
 * The component is based on the [TextInput](#/Forms/TextInput) and [date-fns](https://date-fns.org/)
 * library. It is internally used by the NewDatePicker, and they both share same properties for
 * controlling the allowed input dates etc. DateInput is meant for inquiring known dates whereas
 * the [NewDatePicker](#/Forms/NewDatePicker) is for allowing choosing a date.
 */

export const DateInput = function DateInput(_ref) {
  let {
      allowPastDates = false,
      allowWeekends = true,
      'aria-describedby': ariaDescribedby = undefined,
      'aria-label': ariaLabel = undefined,
      'aria-labelledby': ariaLabelledby = undefined,
      className,
      customErrorMessage,
      customHelpText,
      dateFormat = defaultDateFormat,
      disabled: disabledProp,
      disabledDays,
      fwdRef,
      initialDate = undefined,
      inputProps = undefined,
      invalid,
      isDayBlocked,
      isOutsideRange,
      maxDate,
      minDate,
      onChange,
      required: requiredProp,
      showHelpText = true,
      size: propSize,
      value: dateControlled
    } = _ref,
    wrapperProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const formRowContext = useContext(FormRowContext);
  const {
    spacing
  } = useContext(FormContext);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const initialFormattedDate = !dateControlled && initialDate && validateDate(initialDate) ? format(initialDate, dateFormat) : '';
  const [date, setDate] = useState(initialFormattedDate);
  // Date should be send back through onChange only when onBlur occurs so to display the input value as it's typed, it needs to have its own state
  const [inputValue, setInputValue] = useState(initialFormattedDate);
  const disabled = disabledProp != null ? disabledProp : formRowContext.disabled;
  const required = requiredProp != null ? requiredProp : formRowContext.required;
  const size = propSize != null ? propSize : spacing;
  const helpTextId = useMemo(() => "ds-di-" + uuidv4(), []);
  const errorTextId = useMemo(() => "ds-di-" + uuidv4(), []);
  const lc = useLocale();
  const dateRangeChecks = {
    allowPastDates,
    allowWeekends,
    maxDate,
    minDate
  };
  const parametersForErrorMessage = {
    dateFormat,
    dateRangeChecks,
    disabledDays,
    isDayBlocked,
    isOutsideRange,
    lc
  };
  const mergedDescribedbyIds = classNames(ariaDescribedby, {
    [errorTextId]: errorMessage,
    [helpTextId]: showHelpText
  });
  const displayDateFormat = messages.defaultDisplayDateFormat[lc];
  function combinedValidations(dateToValidate) {
    var _isDayBlocked, _isOutsideRange;
    if (!validateDate(dateToValidate)) {
      return false;
    }
    if (dateFormat === defaultDateFormat && !checkYearLength(inputValue)) {
      return false;
    }
    const isBlocked = (_isDayBlocked = isDayBlocked == null ? void 0 : isDayBlocked(dateToValidate)) != null ? _isDayBlocked : checkIsDayBlocked(dateToValidate, disabledDays);
    const isOutside = (_isOutsideRange = isOutsideRange == null ? void 0 : isOutsideRange(dateToValidate)) != null ? _isOutsideRange : checkIsOutsideRange(dateToValidate, dateRangeChecks);
    return !!(!isOutside && !isBlocked);
  }
  function handleSetValues(dateParam) {
    const formattedDate = format(dateParam, dateFormat);
    setErrorMessage(undefined);
    setDate(formattedDate);
    setInputValue(formattedDate);
  }
  function handleBlur() {
    const onChangeData = {
      date: undefined,
      formattedDate: undefined,
      inputValue: undefined,
      error: undefined,
      errorType: undefined
    };
    if (inputValue) {
      const parsedDate = parse(inputValue, dateFormat, new Date());
      if (combinedValidations(parsedDate)) {
        handleSetValues(parsedDate);
        const formattedDate = format(parsedDate, dateFormat);
        onChangeData.date = parsedDate;
        onChangeData.formattedDate = formattedDate;
        onChangeData.inputValue = formattedDate;
      } else {
        const errorObject = getErrorMessageAndType(parsedDate, parametersForErrorMessage, inputValue && dateFormat === defaultDateFormat ? inputValue : undefined);
        setErrorMessage(customErrorMessage != null ? customErrorMessage : errorObject == null ? void 0 : errorObject.errorMessage);
        setDate('');
        onChangeData.inputValue = inputValue;
        onChangeData.error = customErrorMessage != null ? customErrorMessage : errorObject == null ? void 0 : errorObject.errorMessage;
        onChangeData.errorType = errorObject == null ? void 0 : errorObject.errorType;
      }
    } else {
      // If there was a date set when the input field were emptied, empty the date also
      if (date) {
        setDate('');
      }

      // If there was an error set when the input field were emptied, empty the error also
      if (errorMessage) {
        setErrorMessage(undefined);
      }
    }
    if (onChange) {
      onChange(onChangeData);
    }
  }
  function handleInvalidDate(dateToValidate) {
    if (validateDate(dateToValidate)) {
      const formattedDate = format(dateToValidate, dateFormat);
      setDate(formattedDate);
      setInputValue(formattedDate);
    } else {
      setInputValue(dateToValidate.toString());
    }
    const errorObject = getErrorMessageAndType(dateToValidate, parametersForErrorMessage);
    setErrorMessage(customErrorMessage != null ? customErrorMessage : errorObject == null ? void 0 : errorObject.errorMessage);
  }
  useEffect(() => {
    if (dateControlled) {
      if (combinedValidations(dateControlled)) {
        handleSetValues(dateControlled);
      } else {
        handleInvalidDate(dateControlled);
      }
      if (onChange) {
        const formattedDate = validateDate(dateControlled) ? format(dateControlled, dateFormat) : dateControlled.toString();
        const errorObject = getErrorMessageAndType(dateControlled, parametersForErrorMessage);
        onChange({
          date: dateControlled,
          formattedDate: formattedDate,
          inputValue: formattedDate,
          error: customErrorMessage != null ? customErrorMessage : errorObject == null ? void 0 : errorObject.errorMessage,
          errorType: errorObject == null ? void 0 : errorObject.errorType
        });
      }
    } else if (dateControlled !== undefined && onChange) {
      setDate('');
      setInputValue('');
      setErrorMessage(undefined);
      onChange({
        date: undefined,
        formattedDate: undefined,
        inputValue: undefined,
        error: undefined,
        errorType: undefined
      });
    }
  }, [dateControlled]);
  useEffect(() => {
    if (initialDate && !combinedValidations(initialDate)) {
      handleInvalidDate(initialDate);
    }
  }, []);
  return /*#__PURE__*/React.createElement("div", _extends({}, wrapperProps, {
    className: buildVersionedClassNames('ds-date-input', className),
    ref: fwdRef
  }), /*#__PURE__*/React.createElement(TextInput, _extends({}, inputProps, {
    "aria-describedby": mergedDescribedbyIds,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    amount: false,
    autosize: undefined,
    counter: undefined,
    disabled: disabled,
    invalid: !!errorMessage || invalid,
    onBlur: () => handleBlur(),
    onChange: event => setInputValue(event.target.value),
    required: required,
    rows: 1,
    size: size,
    type: "text",
    unit: null,
    value: inputValue
  })), /*#__PURE__*/React.createElement(InputError, {
    "aria-live": "assertive",
    className: classNames({
      [buildVersionedClass('ds-date-input__error-message--no-bottom-margin')]: showHelpText
    }),
    error: errorMessage,
    id: errorTextId
  }), showHelpText && /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-date-input__help-text'),
    id: helpTextId
  }, customHelpText != null ? customHelpText : messages.instructions[lc].helpText({
    dateFormat: dateFormat === defaultDateFormat ? displayDateFormat : dateFormat
  })));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(DateInput, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'DateInput';
export default Fwd;